import { viewerStrings } from '../copy/strings.js';
import { message, hasText, optionalText } from '../copy/text.js';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import PropertyFilter, {
  type PropertyFilterProps as Cloud,
} from '@cloudscape-design/components/property-filter';
import Alert from '@cloudscape-design/components/alert';
import {
  valueStatusSchema,
  type FieldValuesQuery,
  type RelationshipValuesQuery,
  type RelationshipValuesResult,
} from '@cdr-clarity/contracts';
import type { UnifiedRecordsState } from '../state/unified-records-state.js';
import { filterLabels } from '../state/filter-labels.js';
import { FilterValuePages } from '../state/filter-value-pages.js';
import {
  choiceValue,
  choiceLabel as cloudChoiceLabel,
  cloudProperties,
  interpretationField,
  readCloudFilter,
  writeCloudFilter,
} from '../state/cloud-filter.js';

export function PropertyFilters({
  store,
  values,
  leading,
}: {
  store: UnifiedRecordsState;
  values: (query: RelationshipValuesQuery) => Promise<RelationshipValuesResult>;
  leading?: ReactNode;
}) {
  const state = useSyncExternalStore(store.subscribe, store.getSnapshot);
  const fields = useSyncExternalStore(store.fields.subscribe, store.fields.getSnapshot);
  const [error, setError] = useState('');
  const [options, setOptions] = useState<Cloud.FilteringOption[]>([]);
  const [status, setStatus] = useState<Cloud.StatusType>('finished');
  const [count, setCount] = useState('');
  const [labelled, setLabelled] = useState<{ key: string; query: Cloud.Query } | null>(null);
  const request = useRef(0);
  const paging = useRef(new FilterValuePages<Cloud.FilteringOption>());
  const visible = state.visibleFields;
  const definition = useMemo(() => {
    try {
      return { query: store.definition(), error: '' };
    } catch (reason) {
      return { query: null, error: String(reason) };
    }
  }, [store, state, fields]);
  const predicate = definition.query?.predicate;
  const scope = definition.query?.scope ?? state.scope;
  const labelKey = JSON.stringify([
    scope,
    visible.map((field) => field.id),
    predicate,
    fields.preference.timezone,
  ]);
  const labelValues = useCallback(
    (input: FieldValuesQuery) =>
      values({
        query: {
          ...store.definition(),
          scope: { ...input.scope, collection: 'cdr' },
          predicate: input.predicate,
          order: [],
        },
        field: input.field,
        search: input.search,
        page: input.page,
      }),
    [store, values],
  );
  const adapter = useMemo(() => {
    try {
      return {
        query: writeCloudFilter(predicate, visible, fields.preference.timezone),
        error: definition.error,
      };
    } catch (reason) {
      return { query: { operation: 'and' as const, tokens: [] }, error: String(reason) };
    }
  }, [predicate, visible, fields.preference.timezone, definition.error]);
  useEffect(() => {
    if (adapter.error || labelled?.key === labelKey) return;
    let active = true;
    void filterLabels(predicate, visible, fields.preference.timezone, scope, labelValues)
      .then((query) => {
        if (active) {
          setLabelled({ key: labelKey, query });
          setError('');
        }
      })
      .catch((reason: unknown) => {
        if (active)
          setError(
            message(
              viewerStrings.webComponentsPropertyFilters.savedFilterLabelsCouldNotBeResolved,
              { value1: String(reason) },
            ),
          );
      });
    return () => {
      active = false;
    };
  }, [
    labelKey,
    labelled,
    adapter.error,
    predicate,
    fields.preference.timezone,
    visible,
    scope,
    labelValues,
  ]);
  useEffect(() => {
    ++request.current;
    paging.current = new FilterValuePages<Cloud.FilteringOption>();
    setOptions([]);
    setCount('');
    setStatus('finished');
  }, [state.generation, fields.preference.timezone]);
  useEffect(
    () => () => {
      ++request.current;
    },
    [],
  );
  async function load(detail: Cloud.LoadItemsDetail): Promise<void> {
    const id = ++request.current;
    const key = detail.filteringProperty?.key;
    if (!key) {
      setOptions([]);
      setStatus('finished');
      setCount('');
      return;
    }
    const interpretation = interpretationField(key);
    if (interpretation) {
      setOptions(
        [...valueStatusSchema.options].sort().map((value) => ({ propertyKey: key, value })),
      );
      setStatus('finished');
      setCount('');
      return;
    }
    const field = visible.find((field) => field.id === key);
    if (!field) return;
    const identity = JSON.stringify([key, detail.filteringText]);
    const previous = paging.current.begin(identity, detail.firstPage, detail.samePage);
    if (!previous) return;
    setStatus('loading');
    try {
      const definition = store.definition();
      const result = await values({
        query: definition,
        field: key,
        search: detail.filteringText,
        page: { size: 250, ...(previous.cursor ? { cursor: previous.cursor } : {}) },
      });
      if (id !== request.current) return;
      const next = [
        ...result.values
          .filter(({ cell }) => cell.status === 'known')
          .map(({ cell, counts }) => ({
            propertyKey: key,
            value: choiceValue(cell, field, fields.preference.timezone),
            tags: [
              message(viewerStrings.webExtras.filterValueCounts, {
                calls: counts.primaryRecords.toLocaleString(),
                media: counts.relatedRecords.toLocaleString(),
                rows: counts.memberships.toLocaleString(),
              }),
            ],
          })),
      ];
      // Native token formatters render the shared cell label; choices remain typed in the query.
      const labelled = paging.current.complete(
        previous,
        next.map((option) => ({ ...option, label: cloudChoiceLabel(option.value) })),
        result.nextCursor,
      );
      setOptions(labelled);
      setError('');
      setCount(
        message(viewerStrings.webComponentsPropertyFilters.knownValuesLoadedDistinctValues, {
          value1: labelled.length.toLocaleString(),
          value2: result.total.toLocaleString(),
          value3: result.complete ? '' : viewerStrings.webComponentsPropertyFilters.partialImport,
        }),
      );
      setStatus(result.nextCursor ? 'pending' : 'finished');
    } catch (reason) {
      if (id === request.current) {
        setError(String(reason));
        setStatus('error');
      }
    }
  }
  return (
    <div className="record-filter">
      {hasText(adapter.error || error || '') && (
        <Alert type="error">{adapter.error || error}</Alert>
      )}
      {(adapter.error || error) &&
        (hasText(viewerStrings.webComponentsPropertyFilters.clearIncompatibleFilter) ? (
          <button type="button" onClick={() => store.setFilter(undefined)}>
            {viewerStrings.webComponentsPropertyFilters.clearIncompatibleFilter}
          </button>
        ) : null)}
      <div className="record-filter-row">
        {leading}
        <div className="record-filter-input">
          {!predicate || labelled?.key === labelKey || adapter.error ? (
            <PropertyFilter
              query={labelled?.key === labelKey ? labelled.query : adapter.query}
              filteringProperties={cloudProperties(visible, fields.preference.timezone)}
              filteringOptions={options}
              disabled={!!adapter.error || state.focus !== null}
              disableFreeTextFiltering
              enableTokenGroups
              expandToViewport
              virtualScroll={options.length > 500}
              filteringPlaceholder={
                optionalText(viewerStrings.webComponentsPropertyFilters.filterRecords) ?? ''
              }
              filteringAriaLabel={viewerStrings.webComponentsPropertyFilters.filterRecords2}
              i18nStrings={{
                enteredTextLabel: (value) =>
                  message(viewerStrings.webComponentsPropertyFilters.use, {
                    value1: cloudChoiceLabel(value),
                  }),
              }}
              filteringStatusType={status}
              countText={count}
              filteringLoadingText={viewerStrings.webExtras.filteringLoadingText}
              filteringErrorText={viewerStrings.webExtras.filteringErrorText}
              filteringRecoveryText={viewerStrings.webExtras.filteringRecoveryText}
              filteringFinishedText={viewerStrings.webExtras.filteringFinishedText}
              onLoadItems={({ detail }) => void load(detail)}
              onChange={({ detail }) => {
                try {
                  const predicate = readCloudFilter(detail, visible);
                  const key = JSON.stringify([
                    scope,
                    visible.map((field) => field.id),
                    predicate,
                    fields.preference.timezone,
                  ]);
                  setLabelled({ key, query: detail });
                  store.setFilter(predicate);
                  setError('');
                } catch (reason) {
                  setError(String(reason));
                }
              }}
            />
          ) : hasText(viewerStrings.webComponentsPropertyFilters.loadingSavedFilterLabels) ? (
            <span role="status">
              {viewerStrings.webComponentsPropertyFilters.loadingSavedFilterLabels}
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
}
